import { ref, onUnmounted, type Ref, isRef } from 'vue'
import api from '@/api/axios'

interface HeartbeatResponse {
  success: boolean
  last_activity_at: string
  lock_expires_at: string | null
  status: string
  message?: string
  locked_by?: string
}

interface CheckinLockInfo {
  lockedBy: string | null
  lockExpiresAt: Date | null
  isLocked: boolean
  canResume: boolean
}

/**
 * Accepts a plain number, a Ref<number|null>, or a getter function.
 * The ID is resolved lazily on every heartbeat so it stays reactive.
 */
export function useCheckinHeartbeat(checkinIdInput: number | null | Ref<number | null> | (() => number | null)) {
  const resolveId = (): number | null => {
    const raw = typeof checkinIdInput === 'function'
      ? checkinIdInput()
      : isRef(checkinIdInput)
        ? checkinIdInput.value
        : checkinIdInput
    // Guard against NaN from parseInt(undefined)
    return raw != null && !Number.isNaN(raw) ? raw : null
  }
  const isConnected = ref(true)
  const lastHeartbeat = ref<Date | null>(null)
  const heartbeatError = ref<string | null>(null)
  const lockInfo = ref<CheckinLockInfo>({
    lockedBy: null,
    lockExpiresAt: null,
    isLocked: false,
    canResume: false
  })

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null
  let isActive = false
  const HEARTBEAT_INTERVAL = 60000 // 1 minute

  const sendHeartbeat = async () => {
    const id = resolveId()
    if (!id) return

    try {
      const response = await api.post<HeartbeatResponse>(
        `/api/checker/checkins/${id}/heartbeat`
      )

      if (response.data.success) {
        isConnected.value = true
        lastHeartbeat.value = new Date()
        heartbeatError.value = null

        // Update lock info
        lockInfo.value = {
          lockedBy: null,
          lockExpiresAt: response.data.lock_expires_at
            ? new Date(response.data.lock_expires_at)
            : null,
          isLocked: false,
          canResume: false
        }
      }
    } catch (error: unknown) {
      isConnected.value = false

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { message?: string; locked_by?: string; can_force_resume?: boolean } } }
        if (axiosError.response?.status === 423) {
          // Checkin is locked by another user
          heartbeatError.value = axiosError.response.data?.message || 'Check-In ist gesperrt'
          lockInfo.value = {
            lockedBy: axiosError.response.data?.locked_by || 'Unbekannt',
            lockExpiresAt: null,
            isLocked: true,
            canResume: axiosError.response.data?.can_force_resume || false
          }
        } else if (axiosError.response?.status === 409) {
          // Checkin was force-completed (by dispatcher or cleanup)
          heartbeatError.value = axiosError.response.data?.message || 'Check-In wurde bereits abgeschlossen'
          stopHeartbeat()
        } else if (axiosError.response?.status === 404) {
          // Checkin no longer exists
          heartbeatError.value = 'Check-In nicht mehr vorhanden'
          stopHeartbeat()
        } else {
          heartbeatError.value = 'Verbindungsfehler'
        }
      } else {
        heartbeatError.value = 'Netzwerkfehler'
      }
    }
  }

  const startHeartbeat = () => {
    if (heartbeatInterval) return
    if (!resolveId()) return

    isActive = true

    // Send initial heartbeat
    sendHeartbeat()

    // Set up interval
    heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)

    // Listen for visibility changes (app going to background / coming back)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Listen for page unload (tab close, navigation away, app force-close)
    window.addEventListener('beforeunload', handleBeforeUnload)
  }

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    isActive = false

    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
  }

  const handleVisibilityChange = () => {
    if (!isActive || !resolveId()) return

    if (document.visibilityState === 'visible') {
      // App came back to foreground — immediately send heartbeat to
      // re-validate the checkin and extend the lock
      sendHeartbeat()

      // Restart the interval (it may have been throttled by the browser)
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
      }
      heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
    }
    // When going hidden, the browser may throttle intervals but we keep them
    // running. The lock will expire naturally if the app stays backgrounded.
  }

  const handleBeforeUnload = () => {
    // Use keepalive fetch for reliable lock release on page close/refresh.
    const id = resolveId()
    if (!id) return

    const url = `${api.defaults.baseURL || ''}/api/checker/checkins/${id}/release-lock`
    const token = localStorage.getItem('token')
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // navigator.sendBeacon can only send FormData or Blob, not custom headers.
    // Use a keepalive fetch instead, which supports headers and survives page unload.
    try {
      fetch(url, {
        method: 'POST',
        headers,
        body: '{}',
        keepalive: true
      })
    } catch {
      // Best-effort — ignore failures during unload
    }
  }

  const releaseLock = async () => {
    const id = resolveId()
    if (!id) return

    try {
      await api.post(`/api/checker/checkins/${id}/release-lock`)
    } catch {
      // Ignore errors when releasing lock
    }
  }

  // Clean up on unmount
  onUnmounted(() => {
    stopHeartbeat()
    // Release lock when leaving the page
    releaseLock()
  })

  return {
    isConnected,
    lastHeartbeat,
    heartbeatError,
    lockInfo,
    startHeartbeat,
    stopHeartbeat,
    sendHeartbeat,
    releaseLock
  }
}

// Composable for resuming stale checkins
export function useCheckinResume() {
  const resumeLoading = ref(false)
  const resumeError = ref<string | null>(null)

  const resumeCheckin = async (checkinId: number, force = false) => {
    resumeLoading.value = true
    resumeError.value = null

    try {
      const response = await api.post(`/api/checker/checkins/${checkinId}/resume`, {
        force
      })

      if (response.data.success) {
        return response.data.data
      } else {
        resumeError.value = response.data.message
        return null
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } }
        resumeError.value = axiosError.response?.data?.message || 'Fehler beim Fortsetzen'
      } else {
        resumeError.value = 'Netzwerkfehler'
      }
      return null
    } finally {
      resumeLoading.value = false
    }
  }

  return {
    resumeLoading,
    resumeError,
    resumeCheckin
  }
}

// Composable for dispatcher stale checkin management
export function useStaleCheckins() {
  const staleCheckins = ref<unknown[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const fetchStaleCheckins = async (timeoutMinutes = 120) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.get('/api/dispatcher/stale-checkins', {
        params: { timeout: timeoutMinutes }
      })

      if (response.data.success) {
        staleCheckins.value = response.data.data
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } }
        error.value = axiosError.response?.data?.message || 'Fehler beim Laden'
      } else {
        error.value = 'Netzwerkfehler'
      }
    } finally {
      loading.value = false
    }
  }

  const forceCompleteCheckin = async (
    checkinId: number,
    forwardedToLocationId: number,
    reason?: string
  ) => {
    try {
      const response = await api.post(`/api/dispatcher/checkins/${checkinId}/force-complete`, {
        forwarded_to_location_id: forwardedToLocationId,
        reason
      })

      if (response.data.success) {
        // Remove from list
        staleCheckins.value = staleCheckins.value.filter(
          (c: unknown) => (c as { id: number }).id !== checkinId
        )
        return response.data
      }
      return null
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } }
        throw new Error(axiosError.response?.data?.message || 'Fehler beim Abschliessen')
      }
      throw new Error('Netzwerkfehler')
    }
  }

  return {
    staleCheckins,
    loading,
    error,
    fetchStaleCheckins,
    forceCompleteCheckin
  }
}
